<script>
import ProductDescription from "@js/components/Sections/Product/ProductDescription.vue";
import ImageSlider from "@js/components/Sections/Gallery/ImageSlider.vue";
import {useThemeStore} from "@js/store/ThemeStore.js";
import ProductDeliveryInfo from "@js/components/Sections/Product/ProductDeliveryInfo.vue";
import ProductSelfPickupInfo from "@js/components/Sections/Product/ProductSelfPickupInfo.vue";
import {useProductStore} from "@js/store/ProductStore.js";
import CallbackButton from "@js/components/Buttons/CallbackButton.vue";
import NumberFormatComponent from "@js/components/Formatter/NumberFormatComponent.vue";
import ProductAddToCart from "@js/components/Sections/Product/Components/ProductAddToCart.vue";
import IconShare from "@js/components/Icons/IconShare.vue";
import ProductInfo from "@js/components/Sections/Product/ProductInfo.vue";
import IconFactory from "@js/components/Icons/IconFactory.vue";
import IconDeliveryCar from "@js/components/Icons/IconDeliveryCar.vue";
import {computed, nextTick, onBeforeMount, onMounted, ref, watch} from "vue";
import MobileTopBar from "@js/components/TopBar/MobileTopBar.vue";
import OnlyMobile from "@js/components/DynamicComponents/OnlyMobile.vue";
import OnlyDesktop from "@js/components/DynamicComponents/OnlyDesktop.vue";
import {Link} from "@inertiajs/vue3";
import IconCreditCard from "@js/components/Icons/IconCreditCard.vue";
import ProductCartForm from "@js/components/Sections/Product/ProductCartForm.vue";
import FastBuyCallback from "@js/components/Callbacks/FastBuyCallback.vue";
import Breadcrumbs from "@js/components/Breadcrumbs/Breadcrumbs.vue";
import ProductCharacteristicsFeature from "@js/components/Sections/Product/ProductCharacteristicsFeature.vue";
import TabsComponent from "@js/components/Tabs/TabsComponent.vue";
import ProductAllCharacteristics from "@js/components/Sections/Product/Components/ProductAllCharacteristics.vue";
import ProductFaq from "@js/components/Sections/Product/Components/ProductFaq.vue";
import TextAccordion from "@js/components/Sections/TextBlocks/TextAccordion.vue";
import {useCartStore} from "@js/store/CartStore.js";
import ProductBetonPriceSpecifications from "@js/components/Sections/Product/ProductBetonPriceSpecifications.vue";
import ImageLoader from "@js/components/Loaders/ImageLoader.vue";
import {ecomDetail, mapProduct} from "@js/ecommerce.js";

export default {
    name: "ProductPage",
    props: {
        product: {
            required: true,
        },
        characteristics_grouped: {
            type: Object,
            required: false,
        },
    },
    data() {
        return {
            tabs: [
                {id: 1, title: 'Описание'},
                {id: 2, title: 'Все характеристики'},
                {id: 3, title: 'Частые вопросы'},
            ]
        }
    },
    components: {
        ImageLoader,
        ProductBetonPriceSpecifications,
        TextAccordion,
        ProductFaq,
        ProductAllCharacteristics,
        TabsComponent,
        ProductCharacteristicsFeature,
        Breadcrumbs,
        FastBuyCallback,
        ProductCartForm,
        IconCreditCard,
        Link,
        OnlyDesktop,
        OnlyMobile,
        MobileTopBar,
        IconDeliveryCar,
        IconFactory,
        ProductInfo,
        IconShare,
        ProductAddToCart,
        NumberFormatComponent,
        CallbackButton,
        ProductSelfPickupInfo,
        ProductDeliveryInfo,
        ImageSlider,
        ProductDescription,
    },
    computed: {
        imagesList() {
            let links = []
            if (this.product.media !== undefined && this.product.media !== null && this.product.media.images.length > 0) {
                this.product.media.images.forEach(image => {
                    links.push('/storage/products/' + image)
                })
            } else {
                links.push('/assets/defaults/product-not-found.webp')
            }

            return links
        }
    },
    setup(props) {
        const allCharsRef = ref(null)
        const currentTab = ref(1)

        const themeStore = useThemeStore()
        const productStore = useProductStore()
        const cartStore = useCartStore()

        // hydrate куки до всего
        cartStore.hydrate()

        // регистрируем товар в CartStore (включая бетонные спецификации, если пришли)
        cartStore.registerCatalogProduct(props.product)

        // заполняем ProductStore (для совместимости со старыми компонентами)
        productStore.product = props.product;
        productStore.characteristics_grouped = props.characteristics_grouped;

        // активная цена из CartStore
        const activePrice = computed(() => cartStore.priceActive(productStore.product.id))

        // синхронизируем активную цену в productStore.product.*,
        // чтобы существующие компоненты читали корректную цифру
        const syncProductPrice = () => {
            const p = productStore.product
            const next = Number(activePrice.value || 0)
            p.current_price = next
            p.price = next
            try {
                cartStore.updateCatalogPrice?.(p.id, next)
            } catch (_) {
            }
        }
        // первичная синхронизация
        syncProductPrice()

        // при смене выбора (opts) → пересчитать и обновить schema markup
        watch(activePrice, () => {
            syncProductPrice()
        })

        // остальное как было
        const goToAllDescription = function () {
            nextTick(() => {
                if (allCharsRef.value) {
                    allCharsRef.value.scrollIntoView({behavior: 'smooth', block: 'center'})
                }
            })
        }

        const base_chars = computed(() => (props.characteristics_grouped || []).slice(0, 5))

        onBeforeMount(() => {
            // detail — просмотр карточки товара
            ecomDetail(mapProduct(props.product, { list: 'PDP', position: 1 }))
        })

        return {themeStore, productStore, cartStore, goToAllDescription, base_chars, currentTab}
    }
}
</script>

<template>
    <MobileTopBar/>
    <div class="container">
        <Breadcrumbs/>
        <OnlyMobile :maxSize="1024">
            <h1 class="flex items-center text-2xl md:text-3xl font-bold mb-3">
                {{ productStore.product.title }}
            </h1>
        </OnlyMobile>
        <div class="grid grid-cols-12 gap-y-5 md:gap-y-10 md:gap-x-10 text-gray-900 mb-5">
            <div class="col-span-12 md:col-span-7 lg:col-span-5 order-1 relative">
                <ImageLoader :title="productStore.product.title"
                             :src="imagesList[0]"
                             imgClasses="w-full max-h-[300px] md:max-h-[400px] object-cover object-bottom border border-gray-200 rounded-2xl"/>
            </div>
            <div class="col-span-12 lg:col-span-4 order-3 lg:order-2 flex flex-col gap-3">
                <OnlyDesktop :maxSize="1024">
                    <h1 class="flex items-center text-2xl md:text-3xl font-bold mb-0">
                        {{ product.title }}
                    </h1>
                </OnlyDesktop>

                <!-- 🔹 Новый пикер спецификаций (пишет в CartStore) -->
                <ProductBetonPriceSpecifications
                    :product-id="productStore.product.id"
                    :specs="productStore.product.beton_price_specifications ?? productStore.product.beton_prices ?? []"
                />

                <div>
                    <div class="text-base font-semibold text-gray-900 mb-2">
                        Характеристики
                    </div>
                    <ProductCharacteristicsFeature v-for="char in base_chars"
                                                   :key="char.type_id || char.title"
                                                   :title="char.title" :description="char.values"/>
                </div>
                <div @click.prevent="goToAllDescription" aria-label="Смотреть все характеристики"
                     class="underline text-sm font-medium cursor-pointer md:hover:text-navHoverColor transition">
                    См. все характеристики
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-2.5 mb-5">
                    <Link :href="route('aboutPayment')"
                          class="flex items-center justify-center text-start gap-3 text-sm w-full bg-transparent border border-dashed border-gray-500 text-black p-3 rounded-lg md:hover:bg-baseColor font-semibold transition">
                        <IconCreditCard class="min-w-7 max-w-7 min-h-7 max-h-7"/>
                        Оплата наличными и безналичным
                    </Link>
                    <Link :href="route('deliveryAndShipping')"
                          class="flex items-center justify-center text-start gap-3 text-sm w-full bg-transparent border border-dashed border-gray-500 text-black p-3 rounded-lg md:hover:bg-baseColor font-semibold transition">
                        <IconDeliveryCar class="min-w-7 max-w-7 min-h-7 max-h-7"/>
                        Доставка по МСК:<br class="max-md:hidden"/> от 1 дня
                    </Link>
                </div>
            </div>
            <div class="col-span-12 md:col-span-5 lg:col-span-3 order-2 lg:order-3">
                <ProductCartForm v-if="productStore.product" :key="product.id"/>
            </div>
        </div>
        <div class="grid grid-cols-12 gap-y-10 md:gap-x-10 text-gray-900">
            <div class="col-span-12 md:col-span-8" ref="allCharsRef">
                <div class=" rounded-2xl mb-10">
                    <TabsComponent v-model="currentTab" :tabs="tabs"/>
                    <div class="border rounded-2xl p-5">
                        <div v-show="currentTab === 1" ref="allCharsRef">
                            <TextAccordion :minHeight="285" v-if="productStore.product.media.description">
                                <div class="flex flex-col gap-1 bbp"
                                     v-html="productStore.product.media.description"></div>
                            </TextAccordion>
                            <div v-else class="text-sm text-gray-600 italic">Описания пока нет</div>
                        </div>
                        <div v-show="currentTab === 2">
                            <ProductAllCharacteristics
                                :characteristics_grouped="characteristics_grouped"
                                :category-slug="product.category.slug"/>
                        </div>
                        <div v-show="currentTab === 3">
                            <ProductFaq :items="productStore.product.media.faq"/>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-span-12 md:col-span-4">
                <div class="grid grid-cols-12 gap-y-5 md:gap-x-10 text-gray-900">
                    <div class="col-span-12">
                        <ProductDeliveryInfo/>
                    </div>
                    <div class="col-span-12">
                        <ProductSelfPickupInfo/>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <FastBuyCallback/>
</template>

<style lang="scss">
.bbp {
    /* Кастомизируй при желании через CSS-переменные */
    --bbp-text: #000;
    --bbp-head: #000;
    --bbp-muted: #000;
    --bbp-accent: var(--main-color);
    --bbp-border: #000;

    color: var(--bbp-text);
    font-size: 0.95rem;
    line-height: 1.5;
    word-break: normal;
    overflow-wrap: anywhere;

    > *:first-child {
        margin-top: 0;
    }

    > *:last-child {
        margin-bottom: 0;
    }

    p {
        margin: 0;
    }

    h2, h3 {
        color: var(--bbp-head);
        font-weight: 500;
        line-height: 1.25;
    }

    h2, h3 {
        font-size: clamp(1rem, 0.95rem + 0.4vw, 1.2rem);
    }

    /* Отступы между блоками */
    h2 + ul,
    h3 + ul,
    h3 + p,
    h2 + p {
        margin-top: 0.5em;
    }

    p + h2,
    p + h3,
    ul + h2,
    ul + h3 {
        margin-top: 1em;
    }

    /* Списки — без дефолтных буллитов, со своими маркерами */
    ul {
        list-style: none;
        padding: 0;

        > li {
            position: relative;
            //margin: 0.35em 0;
            padding-left: 1.25em;
        }

        > li::before {
            content: "";
            position: absolute;
            left: 0;
            top: 0.6em;
            width: 0.5em;
            height: 0.5em;
            border-radius: 50%;
            background: var(--bbp-accent);
        }
    }

    /* Мобильная и десктопная подстройка размеров */
    @media (min-width: 768px) {
        ul > li {
            margin: 0.4em 0;
        }
    }

    /* Тёмная схема (если нужна) */
    @media (prefers-color-scheme: dark) {
        --bbp-text: #000; /* gray-200 */
        --bbp-head: #000; /* gray-100 */
        --bbp-muted: #000; /* gray-400 */
        --bbp-border: #000; /* gray-700 */
        --bbp-accent: var(--main-color); /* sky-400 */
    }
}
</style>
